import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { PrimeIcons } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import { Campaign, CampaignControllerService } from 'src/app/admin-api';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  styleUrl: './campaigns.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class CampaignsComponent implements OnInit {
  private campaignService = inject(CampaignControllerService);
  private title = inject(Title);
  private router = inject(Router);

  campaigns: Campaign[] = [];

  dropdownFilters = {
    status: [
      { label: 'Todos', value: '' },
      { label: 'Rascunho', value: 'DRAFT' },
      { label: 'Planejamento', value: 'PLANNING' },
      { label: 'Agendada', value: 'SCHEDULED' },
      { label: 'Ativa', value: 'ACTIVE' },
      { label: 'Pausada', value: 'PAUSED' },
      { label: 'Finalizada', value: 'FINISHED' },
      { label: 'Cancelada', value: 'CANCELLED' },
      { label: 'Arquivada', value: 'ARCHIVED' }
    ]
  };

  columns = [
    new TableColumn.Builder()
      .setHeader('ID')
      .setField('campaignId')
      .setRouterLink((campaign: Campaign) => `/marketing/campaigns/${campaign.campaignId}`)
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Nome')
      .setField('name')
      .setRouterLink((campaign: Campaign) => `/marketing/campaigns/${campaign.campaignId}`)
      .setType('text')
      .setFilter(true)
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setType('status')
      .setStatusFunction((campaign: Campaign) => {
        const status = (campaign.status || '').toUpperCase();
        if (status === 'ACTIVE') return 1;
        if (status === 'FINISHED' || status === 'CANCELLED' || status === 'ARCHIVED') return -1;
        return 0;
      })
      .setDisplayFunction((campaign: Campaign) => this.mapStatusLabel(campaign.status))
      .setFilter(true)
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Início')
      .setField('startDate')
      .setType('date')
      .setDisplayFunction((campaign: Campaign) => this.formatDateOnly(campaign.startDate))
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Término')
      .setField('endDate')
      .setType('date')
      .setDisplayFunction((campaign: Campaign) => this.formatDateOnly(campaign.endDate))
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo')
      .setField('ruleType')
      .setType('text')
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Ações')
      .setType('button')
      .setFilter(false)
      .build()
  ];

  actionButtons = [
    new TableActionButton.Builder()
      .setLabel('Gerar brindes')
      .setAction((campaign: Campaign) =>
        this.router.navigate(['/marketing/campaigns', campaign.campaignId, 'generate'])
      )
      .setTooltip('Gerar brindes')
      .setDisplay((campaign: Campaign) => (campaign.status || '').toUpperCase() === 'ACTIVE')
      .build(),
    new TableActionButton.Builder()
      .setLabel('')
      .setIcon(PrimeIcons.CLOCK)
      .setAction((campaign: Campaign) =>
        this.router.navigate(['/marketing/campaigns', campaign.campaignId, 'history'])
      )
      .setTooltip('Histórico da campanha')
      .setDisplay((campaign: Campaign) => (campaign.status || '').toUpperCase() === 'FINISHED')
      .setRounded(true)
      .setText(true)
      .setSize('small')
      .build()
  ];

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Marketing - Campanhas');
    await this.loadCampaigns();
  }

  async loadCampaigns(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.campaigns = await lastValueFrom(
        this.campaignService.listCampaigns().pipe(map((data) => data.result))
      );
      this.campaigns.forEach((c: any) => {
        c._sortOrder =
          ((c.status || '').toUpperCase() === 'ACTIVE' ? 0 : 1_000_000) - (c.campaignId || 0);
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private mapStatusLabel(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'DRAFT': return 'Rascunho';
      case 'PLANNING': return 'Planejamento';
      case 'SCHEDULED': return 'Agendada';
      case 'ACTIVE': return 'Ativa';
      case 'PAUSED': return 'Pausada';
      case 'FINISHED': return 'Finalizada';
      case 'CANCELLED': return 'Cancelada';
      case 'ARCHIVED': return 'Arquivada';
      default: return status || '-';
    }
  }

  private formatDateOnly(dateStr?: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
}
